import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { orderService } from '../services/orderService';
import { AlertType, AlertVariant } from '../components/alert/Alert';
import { AlertState } from '../components/alert/AlertsWrapper';
import { TOrder, TOrdersDashboard, TOrdersList } from '../context/OrdersProvider';
import { handleApiError } from '../lib/errorHandler';

interface OrderListOptions {
    search?: string;
    status?: string;
    date_after?: string,
    date_before?: string,
    target_device?: string
}

export function useOrderOperations() {
    const [orderDetail, setOrderDetail] = useState<TOrder | null>(null);
    const [ordersList, setOrdersList] = useState<TOrdersList>({
        count: 0,
        results: [],
    });
    const [orderDetailLoading, setOrderDetailLoading] = useState(false);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const { t } = useTranslation();
    const i18nOrderDirectory = (key: string): string =>
        t(`orders-directory.${key}`);

    const getOrderList = async (
        options: OrderListOptions,
        limit: number,
        offset: number,
        setAlertMessages: (alert: AlertState | null) => void
    ) => {
        setOrdersLoading(true);
        try {
            const response = await orderService.getOrders({
                limit,
                offset,
                search: options.search,
                status: options.status,
                date_after: options.date_after,
                date_before: options.date_before,
                target_device: options.target_device
            });
            if (response.data) {
                setOrdersList(response.data);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.orders.loadingList'), "orders");
            setAlertMessages({
                type: AlertType.error,
                messages: serverError,
                variant: AlertVariant.Swimming,
            });
        } finally {
            setOrdersLoading(false);
        }
    };

    const getOrderDetail = async (
        id: string,
        setAlertMessages: (alert: AlertState | null) => void
    ) => {
        setOrderDetailLoading(true);
        try {
            const response = await orderService.getOrderDetail(id);
            if (response.data) {
                setOrderDetail(response.data);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.orders.loadingDetail'), "orders");
            setAlertMessages({
                type: AlertType.error,
                messages: serverError,
                variant: AlertVariant.Swimming,
            });
        } finally {
            setOrderDetailLoading(false);
        }
    };

    const getOrderDashboard = async (
        setAlertMessages: (alert: AlertState | null) => void
    ): Promise<TOrdersDashboard | undefined> => {
        setDashboardLoading(true);
        try {
            const response = await orderService.getOrderDashboard();
            return response.data;
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.orders.loadingDashboard'), "orders");
            setAlertMessages({
                type: AlertType.error,
                messages: serverError,
                variant: AlertVariant.Swimming,
            });
        } finally {
            setDashboardLoading(false);
        }
    };

    const getDeviceSelector = async (
        setAlertMessages: (alert: AlertState | null) => void
    ): Promise<any> => {
        try {
            const response = await orderService.getDeviceSelector();
            return response.data;
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.orders.loadingSelectors'), "orders");
            setAlertMessages({
                type: AlertType.error,
                messages: serverError,
                variant: AlertVariant.Swimming,
            });
        }
    };

    const sendOrder = async (
        id: string,
        device: string | undefined,
        setAlertMessages: (alert: AlertState | null) => void
    ): Promise<string | undefined> => {
        try {
            const response = await orderService.sendOrder(id, device);
            setAlertMessages({
                type: AlertType.success,
                messages: i18nOrderDirectory('send-success'),
                variant: AlertVariant.Swimming,
            });
            return response.data;
        } catch (error) {
            const serverError = handleApiError(error, t('errors.orders.sending'), "orders");
            setAlertMessages({
                type: AlertType.error,
                messages: serverError,
                variant: AlertVariant.Swimming,
            });
        }
    };

    return {
        orderDetail,
        ordersList,
        orderDetailLoading,
        ordersLoading,
        dashboardLoading,
        getOrderList,
        getOrderDetail,
        getOrderDashboard,
        getDeviceSelector,
        sendOrder,
    };
}
